import React, { useMemo, useState } from 'react';
import { Loader2, Globe } from 'lucide-react';
import { expertDashboardApi, getErrorMessage } from '../../../lib/http';
import { ExpertProfile } from '../../../types';
import toast from 'react-hot-toast';

interface BookingPreferencesSettingsProps {
  profile: ExpertProfile;
  onUpdate: (profile: ExpertProfile) => void;
}

// IANA timezones supported by the browser. Falls back to a short curated list
// on engines that don't expose Intl.supportedValuesOf (older Safari) so the
// picker never renders empty.
const FALLBACK_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Kyiv',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
];

function getAllTimezones(): string[] {
  const fn = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  if (typeof fn === 'function') {
    try {
      return fn('timeZone');
    } catch {
      return FALLBACK_TIMEZONES;
    }
  }
  return FALLBACK_TIMEZONES;
}

export const BookingPreferencesSettings: React.FC<BookingPreferencesSettingsProps> = ({
  profile,
  onUpdate,
}) => {
  const [timezone, setTimezone] = useState(profile.timezone);
  const [isBookable, setIsBookable] = useState(profile.is_bookable);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timezones = useMemo(() => getAllTimezones(), []);
  const hasChanges = timezone !== profile.timezone || isBookable !== profile.is_bookable;

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await expertDashboardApi.profile.update({
        timezone,
        is_bookable: isBookable,
      } as any);
      onUpdate(response.data);
      toast.success('Booking preferences updated!');
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-pink-100 rounded-lg">
          <Globe className="w-5 h-5 text-pink-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Booking Preferences</h3>
          <p className="text-sm text-gray-500">
            Timezone for your schedule and whether you accept new bookings
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-5">
        {/* Timezone */}
        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
            Timezone
          </label>
          <select
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-white"
          >
            {/* Include current value even if not in the list, so the select never shows a value missing from options. */}
            {!timezones.includes(timezone) && (
              <option value={timezone}>{timezone}</option>
            )}
            {timezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            All booking times — both yours and clients' views — are anchored to this timezone.
          </p>
        </div>

        {/* Available for booking */}
        <div>
          <div className="flex items-center justify-between py-2">
            <div className="flex-1 pr-4">
              <h4 className="text-sm font-medium text-gray-900">Available for booking</h4>
              <p className="text-sm text-gray-500 mt-1">
                When off, clients won't see new booking slots. Existing bookings stay scheduled.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsBookable((value) => !value)}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2
                ${isBookable ? 'bg-pink-600' : 'bg-gray-200'}
              `}
              aria-pressed={isBookable}
            >
              <span className="sr-only">Toggle availability for booking</span>
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                  transition duration-200 ease-in-out
                  ${isBookable ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
        >
          {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default BookingPreferencesSettings;
